import { extendTheme, theme } from '@chakra-ui/react';
import { BannerVariant } from "../../components/Banner";
import { ButtonVariant } from "../../components/Button";
import { CircleButtonVariant } from "../../components/CircleButton";
import { ComponentState } from "./common";
import { ModalVariant, ModalBodyVariant } from "../../components/modal";
import { RadioGroupVariant } from "../../components/RadioGroup";
import { StatusPillVariant } from "../../components/StatusPill";
import { ToggleSwitchVariant } from "../../components/ToggleSwitch";
var CheckoutFormVariant = /*#__PURE__*/function (CheckoutFormVariant) {
  CheckoutFormVariant["Dark"] = "dark";
  CheckoutFormVariant["Light"] = "light";
  return CheckoutFormVariant;
}(CheckoutFormVariant || {});
var breakpoints = {
  base: '0',
  sm: '480',
  md: '768',
  lg: '1200',
  xl: '1900',
  '2xl': '2400'
};
var breakpointUnits = {
  base: '0px',
  sm: '480px',
  md: '768px',
  lg: '1200px',
  xl: '1900px',
  '2xl': '2400px'
};
function convertHexToRgba(hex, alpha) {
  if (alpha === void 0) {
    alpha = 1;
  }
  var [, r1, r2, g1, g2, b1, b2] = Array.from(hex);
  var blue = parseInt("" + b1 + b2, 16);
  var green = parseInt("" + g1 + g2, 16);
  var red = parseInt("" + r1 + r2, 16);
  if (alpha > 1) {
    alpha = 1;
  } else if (alpha < 0) {
    alpha = 0;
  }
  return "rgba(" + red + "," + green + "," + blue + "," + alpha + ")";
}
var customColors = {
  blue: {
    100: '#D3EFF8',
    200: '#A7DFF1',
    300: '#7BCEEA',
    400: '#49BDE4',
    500: '#23AEDC',
    600: '#1C8BB0',
    700: '#156884'
  },
  navy: {
    100: '#69ACC3',
    200: '#4797B2',
    300: '#39788D',
    400: '#2A5969',
    500: '#1B3A45',
    600: '#0D1B20',
    700: '#000000'
  },
  green: {
    100: '#CFEACF',
    200: '#ACDAAC',
    300: '#89CA89',
    400: '#66BA66',
    500: '#4AA34A',
    600: '#3A803A',
    700: '#2A5D2A'
  },
  orange: {
    100: '#FDE19C',
    200: '#FDD269',
    300: '#FCC337',
    400: '#FBB405',
    500: '#CA9003',
    600: '#986D02',
    700: '#654902'
  },
  red: {
    100: '#FFE9EA',
    200: '#FFB6B9',
    300: '#FF8389',
    400: '#FF5058',
    500: '#FF1D27',
    600: '#E9000B',
    700: '#B60008'
  },
  gray: {
    100: '#F6F8F9',
    200: '#D9E0E3',
    300: '#BCC8CD',
    400: '#9FB0B7',
    500: '#8298A1',
    600: '#677F89',
    700: '#51646C'
  },
  boxShadow: '0 4px 16px rgba(159, 176, 183, 0.3)'
};
var customFonts = {
  body: 'azo-sans-web, Helvetica, Arial, sans-serif',
  heading: 'azo-sans-web, Helvetica, Arial, sans-serif',
  mono: 'azo-sans-web, Helvetica, Arial, sans-serif'
};
var customFontSizes = {
  sm: '12px',
  md: '14px',
  lg: '18px',
  xl: '22px',
  '2xl': '26px'
};
var customFontWeights = {
  light: '300',
  medium: '500',
  normal: 'normal'
};
var customLineHeights = {
  sm: '20px',
  md: '24px',
  lg: '24px',
  xl: '24px',
  '2xl': '32px'
};
var textInputBaseStyle = {
  backgroundColor: '#FFFFFF',
  borderColor: customColors.gray[200],
  color: customColors.gray[500],
  fontSize: customFontSizes.md,
  fontWeight: customFontWeights.light,
  lineHeight: customLineHeights.md,
  placeholderColor: customColors.gray[400],
  smallScreen: {
    fontSize: '16px'
  },
  [ComponentState.Disabled]: {
    backgroundColor: customColors.gray[100]
  },
  [ComponentState.Focus]: {
    borderColor: customColors.blue[400]
  },
  [ComponentState.Invalid]: {
    borderColor: customColors.red[400]
  },
  [ComponentState.Hover]: {
    borderColor: customColors.gray[400]
  }
};
var gutters = {
  sm: '10px',
  md: '20px'
};
var spacing = {
  sm: '4px',
  md: '8px',
  lg: '12px',
  xl: '14px'
};
var components = {
  Banner: {
    variants: {
      [BannerVariant.Danger]: {
        backgroundColor: convertHexToRgba(customColors.red[100], 0.1),
        border: "1px solid " + customColors.red[600],
        svg: {
          color: customColors.red[600]
        }
      },
      [BannerVariant.Neutral]: {
        backgroundColor: convertHexToRgba(customColors.blue[100], 0.1),
        border: "1px solid " + customColors.blue[600],
        svg: {
          color: customColors.blue[600]
        }
      },
      [BannerVariant.Warning]: {
        backgroundColor: convertHexToRgba(customColors.orange[100], 0.1),
        border: "1px solid " + customColors.orange[400],
        svg: {
          color: customColors.orange[400]
        }
      }
    }
  },
  Button: {
    variants: {
      [ButtonVariant.Primary]: {
        [ComponentState.Default]: {
          backgroundColor: customColors.blue[400],
          borderColor: customColors.blue[400],
          color: '#FFFFFF'
        },
        [ComponentState.Hover]: {
          backgroundColor: customColors.blue[700],
          borderColor: customColors.blue[700],
          color: '#FFFFFF'
        },
        [ComponentState.Loaded]: {
          backgroundColor: customColors.green[500],
          borderColor: customColors.green[500],
          color: '#FFFFFF'
        },
        [ComponentState.Loading]: {
          backgroundColor: customColors.blue[700],
          borderColor: customColors.blue[700]
        }
      },
      [ButtonVariant.Secondary]: {
        [ComponentState.Default]: {
          backgroundColor: customColors.gray[100],
          borderColor: customColors.gray[200],
          color: customColors.gray[600]
        },
        [ComponentState.Hover]: {
          backgroundColor: customColors.gray[200],
          borderColor: customColors.gray[300],
          color: customColors.gray[600]
        },
        [ComponentState.Loaded]: {
          backgroundColor: customColors.green[500],
          borderColor: customColors.green[500],
          color: '#FFFFFF'
        },
        [ComponentState.Loading]: {
          backgroundColor: customColors.gray[200],
          borderColor: customColors.gray[300]
        }
      },
      [ButtonVariant.Tertiary]: {
        [ComponentState.Default]: {
          backgroundColor: '#FFFFFF',
          borderColor: customColors.gray[200],
          color: customColors.blue[400]
        },
        [ComponentState.Hover]: {
          backgroundColor: '#FFFFFF',
          borderColor: customColors.gray[300],
          color: customColors.blue[700]
        },
        [ComponentState.Loaded]: {
          backgroundColor: customColors.green[500],
          borderColor: customColors.green[500],
          color: '#FFFFFF'
        },
        [ComponentState.Loading]: {
          backgroundColor: '#FFFFFF',
          borderColor: customColors.gray[300]
        }
      },
      [ButtonVariant.Dismissive]: {
        [ComponentState.Default]: {
          backgroundColor: '#FFFFFF',
          borderColor: 'transparent',
          color: customColors.gray[600]
        },
        [ComponentState.Hover]: {
          backgroundColor: customColors.gray[100],
          borderColor: 'transparent',
          color: customColors.gray[600]
        },
        [ComponentState.Loaded]: {
          backgroundColor: customColors.green[500],
          borderColor: customColors.green[500],
          color: '#FFFFFF'
        },
        [ComponentState.Loading]: {
          backgroundColor: customColors.gray[100]
        }
      },
      [ButtonVariant.Destructive]: {
        [ComponentState.Default]: {
          backgroundColor: customColors.red[400],
          borderColor: customColors.red[400],
          color: '#FFFFFF'
        },
        [ComponentState.Hover]: {
          backgroundColor: customColors.red[700],
          borderColor: customColors.red[700],
          color: '#FFFFFF'
        },
        [ComponentState.Loaded]: {
          backgroundColor: customColors.green[500],
          borderColor: customColors.green[500],
          color: '#FFFFFF'
        },
        [ComponentState.Loading]: {
          backgroundColor: customColors.red[700],
          borderColor: customColors.red[700]
        }
      }
    }
  },
  ButtonGroup: {
    baseStyle: {
      activeColor: customColors.blue[500],
      backgroundColor: 'transparent',
      inactiveColor: customColors.gray[200]
    }
  },
  Checkbox: {
    customStyle: {
      backgroundColor: customColors.gray[100],
      borderColor: customColors.gray[400],
      checkColor: customColors.blue[400],
      checkWidth: '8px',
      width: '16px',
      [ComponentState.Disabled]: {
        backgroundColor: customColors.gray[100],
        borderColor: customColors.gray[200],
        checkColor: customColors.blue[100]
      }
    }
  },
  CheckoutForm: {
    baseStyle: {
      accentColor: customColors.blue[500],
      emptyBorderColor: '#DDE2E5',
      fontPairingId: 1,
      headingColor: customColors.gray[400],
      inputBorderColor: '#B3C1C6',
      inputBorderColorError: customColors.red[400],
      inputPlaceholderColor: customColors.gray[400],
      linkColor: customColors.blue[400]
    },
    variants: {
      [CheckoutFormVariant.Dark]: {
        backgroundColor: '#2D2D2D',
        completedInputBorderColor: '#DDE2E5',
        inputBackgroundColor: '#242424',
        inputTextColor: customColors.gray[100],
        secondaryColor: '#242424',
        selectBorderColor: '#DDE2E5'
      },
      [CheckoutFormVariant.Light]: {
        backgroundColor: customColors.gray[100],
        completedInputBorderColor: '#B3C1C6',
        inputBackgroundColor: customColors.gray[100],
        inputTextColor: '#A5A5A5',
        secondaryColor: '#FFFFFF',
        selectBorderColor: '#DDE2E5'
      }
    }
  },
  CircleButton: {
    variants: {
      [CircleButtonVariant.Back]: {
        [ComponentState.Default]: {
          borderColor: customColors.blue[200],
          fill: customColors.blue[400]
        }
      },
      [CircleButtonVariant.Close]: {
        [ComponentState.Default]: {
          borderColor: customColors.blue[200],
          fill: customColors.blue[400]
        }
      },
      [CircleButtonVariant.Help]: {
        [ComponentState.Default]: {
          borderColor: customColors.gray[200],
          fill: customColors.gray[400]
        }
      }
    }
  },
  DatePicker: {
    baseStyle: {
      borderColor: customColors.gray[200],
      dayColor: customColors.gray[700],
      dayDisabledColor: customColors.gray[300],
      dayHeaderColor: customColors.gray[500],
      monthColor: customColors.gray[600],
      monthDisabledColor: customColors.gray[300],
      navigationButtonColor: customColors.blue[400],
      rangeBackgroundColor: customColors.blue[100],
      rangeColor: customColors.blue[700],
      selectedColor: customColors.blue[400]
    }
  },
  EditableTable: {
    baseStyle: {
      cell: {
        [ComponentState.Default]: {
          borderColor: customColors.gray[300],
          color: customColors.gray[700]
        },
        [ComponentState.Disabled]: {
          color: customColors.gray[500]
        }
      },
      column: {
        [ComponentState.Default]: {
          headerTextColor: customColors.navy[400]
        }
      },
      row: {
        [ComponentState.Default]: {
          backgroundColorEven: 'white',
          backgroundColorOdd: customColors.gray[100]
        },
        [ComponentState.Selected]: {
          backgroundColorSelected: customColors.navy[100]
        }
      }
    }
  },
  FormElement: {
    baseStyle: {
      marginBottom: '0px'
    }
  },
  InfoIcon: {
    baseStyle: {
      color: customColors.blue[600]
    }
  },
  Label: {
    baseStyle: {
      color: customColors.navy[400],
      fontSize: customFontSizes.md,
      fontWeight: customFontWeights.medium,
      lineHeight: customLineHeights.md,
      marginBottom: '4px'
    }
  },
  LabelError: {
    baseStyle: {
      color: customColors.red[400],
      fontSize: customFontSizes.sm,
      fontWeight: customFontWeights.light,
      lineHeight: customLineHeights.sm
    }
  },
  LabelSubtext: {
    baseStyle: {
      color: customColors.gray[500],
      lineHeight: '20px'
    }
  },
  Menu: {
    baseStyle: () => ({
      borderColor: customColors.gray[200],
      menuItem: {
        color: customColors.gray[600],
        fontWeight: customFontWeights.normal
      },
      menuItemActive: {
        color: customColors.blue[600]
      },
      menuItemHover: {
        backgroundColor: customColors.gray[100]
      }
    })
  },
  Modal: {
    variants: {
      [ModalVariant.Small]: {
        body: {
          backgroundColor: '#FFFFFF',
          maxWidth: 'initial',
          paddingBottom: '24px',
          paddingTop: '16px',
          variants: {
            [ModalBodyVariant.Default]: {},
            [ModalBodyVariant.FullWidth]: {
              maxWidth: '100%',
              paddingBottom: 0,
              paddingTop: 0,
              paddingLeft: 0,
              paddingRight: 0
            }
          }
        },
        borderBottom: 'none',
        borderRadius: '3px',
        header: {
          borderBottom: 'none'
        },
        height: 'auto',
        marginBottom: '60px',
        marginTop: '60px',
        maxWidth: '454px',
        minHeight: 'initial',
        paddingLeft: '40px',
        paddingRight: '40px'
      },
      [ModalVariant.Large]: {
        body: {
          backgroundColor: '#FFFFFF',
          maxWidth: 'initial',
          paddingBottom: '24px',
          paddingTop: '16px',
          variants: {
            [ModalBodyVariant.Default]: {},
            [ModalBodyVariant.FullWidth]: {
              maxWidth: '100%',
              paddingBottom: 0,
              paddingTop: 0,
              paddingLeft: 0,
              paddingRight: 0
            }
          }
        },
        borderRadius: '3px',
        header: {
          borderBottom: "1px solid " + customColors.gray[200]
        },
        height: 'inherit',
        marginBottom: '60px',
        marginTop: '60px',
        maxWidth: '75%',
        minHeight: '80vh',
        paddingLeft: '24px',
        paddingRight: '24px'
      },
      [ModalVariant.Fullscreen]: {
        body: {
          backgroundColor: '#FFFFFF',
          maxWidth: '800px',
          paddingBottom: '24px',
          paddingTop: '16px',
          variants: {
            [ModalBodyVariant.Default]: {},
            [ModalBodyVariant.FullWidth]: {
              maxWidth: '100%',
              paddingBottom: 0,
              paddingTop: 0,
              paddingLeft: 0,
              paddingRight: 0
            }
          }
        },
        borderRadius: '0',
        header: {
          borderBottom: 'none'
        },
        height: '100%',
        marginBottom: '0',
        marginTop: '0',
        maxWidth: '100%',
        minHeight: '100vh',
        paddingLeft: '24px',
        paddingRight: '24px'
      }
    }
  },
  OnboardingStepper: {
    baseStyle: {
      backgroundColor: customColors.gray[100],
      completeBackgroundColor: customColors.green[500],
      completeCheckColor: theme.colors.white,
      countSmallScreenFontSize: theme.fontSizes.sm,
      label: {
        activeFontWeight: theme.fontWeights.medium,
        fontWeight: theme.fontWeights.normal,
        smallScreen: {
          fontSize: '16px',
          fontWeight: theme.fontWeights.medium
        }
      },
      lineBackgroundColor: customColors.gray[200],
      stepIcon: {
        activeTextFontWeight: theme.fontWeights.medium,
        circleStroke: customColors.navy[500],
        textFontWeight: theme.fontWeights.normal
      }
    }
  },
  OptionBox: {
    baseStyle: {
      [ComponentState.Selected]: {
        borderWidth: '2px',
        borderColor: customColors.blue[400],
        boxShadow: customColors.boxShadow,
        padding: '39px'
      },
      [ComponentState.Default]: {
        borderWidth: '1px',
        borderColor: customColors.gray[200],
        padding: '40px'
      }
    }
  },
  OrderSummary: {
    baseStyle: {
      accentColor: customColors.blue[400],
      fontPairingId: 1
    }
  },
  Pagination: {
    baseStyle: {
      borderColor: customColors.gray[200],
      buttonColor: customColors.blue[400],
      pageCountColor: customColors.gray[600],
      [ComponentState.Disabled]: {
        borderColor: customColors.gray[200],
        buttonColor: customColors.gray[200],
        pageCountColor: customColors.gray[200]
      }
    }
  },
  RadioGroup: {
    customStyle: {
      content: {
        color: customColors.navy[700]
      },
      input: {
        borderColor: customColors.gray[400],
        color: customColors.blue[400]
      },
      spacing: '12px'
    },
    variants: {
      [RadioGroupVariant.Bordered]: {
        radio: {
          border: "1px solid " + customColors.gray[200],
          borderRadius: '4px',
          cursor: 'pointer',
          lineHeight: '16px',
          padding: '16px',
          [ComponentState.Checked]: {
            border: "2px solid " + customColors.blue[400]
          }
        }
      },
      [RadioGroupVariant.Default]: {
        radio: {
          lineHeight: '24px'
        }
      }
    }
  },
  RadioTileGroup: {
    baseStyle: {
      background: '#FFFFFF',
      boxShadow: "1px 3px 10px 1px " + customColors.gray[200],
      borderColor: customColors.gray[400],
      color: customColors.navy[400],
      selectedBorder: "2px solid " + customColors.blue[400],
      [ComponentState.Disabled]: {
        backgroundColor: customColors.gray[100]
      }
    }
  },
  ResponsiveNav: {
    baseStyle: {
      borderColor: customColors.gray[200],
      burgerMenu: {
        color: customColors.gray[600]
      },
      navLink: {
        color: customColors.gray[600],
        fontWeight: customFontWeights.normal
      },
      navLinkActive: {
        color: customColors.blue[600]
      },
      smallScreenNav: {
        closeButton: {
          color: '#FFFFFF'
        },
        accordionHeader: {
          color: customColors.navy[400]
        },
        accordionItem: {
          fontSize: customFontSizes.md,
          fontWeight: customFontWeights.normal,
          lineHeight: customLineHeights.md
        }
      }
    }
  },
  Select: {
    baseStyle: () => ({
      backgroundColor: 'none',
      borderColor: customColors.gray[200],
      optionBackgroundColor: '#FFFFFF',
      optionColor: customColors.gray[600],
      optionFontSize: customFontSizes.md,
      placeholderColor: customColors.gray[600],
      placeholderFontSize: customFontSizes.md,
      singleValueFontSize: customFontSizes.md,
      valueColor: customColors.gray[600],
      [ComponentState.Disabled]: {
        placeholderColor: customColors.gray[400],
        valueColor: customColors.gray[400]
      },
      [ComponentState.Hover]: {
        optionColor: '#FFFFFF',
        optionBackgroundColor: customColors.blue[400]
      },
      [ComponentState.Invalid]: {
        borderColor: customColors.red[400]
      }
    })
  },
  SelectChecklist: {
    baseStyle: {
      backgroundColor: customColors.gray[100],
      backgroundColorMobile: '#FFFFFF',
      borderColor: customColors.gray[200],
      optionBackgroundColor: '#FFFFFF',
      optionColor: customColors.gray[600],
      optionFontSize: customFontSizes.md,
      placeholderColor: customColors.navy[400],
      placeholderFontSize: customFontSizes.md,
      placeholderFontSizeMobile: customFontSizes.lg,
      singleValueFontSize: customFontSizes.md,
      valueColor: customColors.gray[600],
      [ComponentState.Disabled]: {
        placeholderColor: customColors.gray[400],
        valueColor: customColors.gray[400]
      },
      [ComponentState.Hover]: {
        optionColor: '#FFFFFF',
        optionBackgroundColor: customColors.blue[400]
      },
      [ComponentState.Invalid]: {
        borderColor: customColors.red[400]
      }
    }
  },
  StatusPill: {
    variants: {
      [StatusPillVariant.Danger]: {
        backgroundColor: convertHexToRgba(customColors.red[200], 0.5),
        color: customColors.gray[700]
      },
      [StatusPillVariant.Neutral]: {
        backgroundColor: convertHexToRgba(customColors.gray[200], 0.5),
        color: customColors.gray[700]
      },
      [StatusPillVariant.Success]: {
        backgroundColor: convertHexToRgba(customColors.green[200], 0.5),
        color: customColors.gray[700]
      },
      [StatusPillVariant.Warning]: {
        backgroundColor: convertHexToRgba(customColors.orange[200], 0.5),
        color: customColors.gray[700]
      }
    }
  },
  TabControl: {
    baseStyle: {
      borderColor: customColors.gray[200],
      color: customColors.blue[400],
      fontSize: customFontSizes.sm,
      lineHeight: customLineHeights.sm,
      [ComponentState.Selected]: {
        borderColor: customColors.navy[500],
        color: customColors.navy[500]
      }
    }
  },
  Table: {
    baseStyle: {
      borderColor: customColors.gray[200],
      borderRadius: '4px'
    }
  },
  TextArea: {
    baseStyle: textInputBaseStyle
  },
  TextInput: {
    baseStyle: textInputBaseStyle
  },
  ToggleSwitch: {
    baseStyle: {
      disabledLabelColor: customColors.gray[400],
      height: '12px',
      throw: {
        backgroundColor: customColors.gray[100],
        borderColor: customColors.gray[500],
        size: '20px'
      },
      width: '32px'
    },
    variants: {
      [ToggleSwitchVariant.Large]: {
        backgroundColor: customColors.blue[400]
      }
    }
  },
  Tooltip: {
    baseStyle: () => ({
      arrowShadowColor: 'rgba(42, 89, 89, 0.3)',
      color: customColors.gray[700],
      shadowColor: 'rgba(42, 89, 89, 0.4)'
    })
  }
};
var {
  md,
  lg,
  xl
} = breakpoints;
var responsive = {
  extraLargeScreenOnly: rules => "\n        @media only screen and (min-width: " + (Number(xl) + 1) + "px) {\n            " + rules + "\n        }\n    ",
  largeScreenDown: rules => "\n        @media only screen and (max-width: " + xl + "px) {\n            " + rules + "\n        }\n    ",
  largeScreenOnly: rules => "\n        @media only screen and (min-width: " + (Number(lg) + 1) + "px) and (max-width: " + xl + "px) {\n            " + rules + "\n        }\n    ",
  largeScreenUp: rules => "\n        @media only screen and (min-width: " + (Number(lg) + 1) + "px) {\n            " + rules + "\n        }\n    ",
  mediumScreenDown: rules => "\n        @media only screen and (max-width: " + lg + "px) {\n            " + rules + "\n        }\n    ",
  mediumScreenOnly: rules => "\n        @media only screen and (min-width: " + (Number(md) + 1) + "px) and (max-width: " + lg + "px) {\n            " + rules + "\n        }\n    ",
  mediumScreenUp: rules => "\n        @media only screen and (min-width: " + (Number(md) + 1) + "px) {\n            " + rules + "\n        }\n    ",
  smallScreenOnly: rules => "\n        @media only screen and (max-width: " + md + "px) {\n            " + rules + "\n        }\n    "
};
var globalStyles = () => ({
  body: {
    bg: null,
    color: null,
    fontFamily: null,
    lineHeight: null,
    transition: null
  },
  '*::placeholder': {
    color: null
  },
  '*, *::before, &::after': {
    borderColor: null,
    wordWrap: null
  }
});
var spTheme = extendTheme({
  breakpoints: breakpointUnits,
  colors: customColors,
  // @ts-ignore
  components,
  fontColor: {
    default: customColors.navy[400]
  },
  fonts: customFonts,
  fontSizes: customFontSizes,
  fontWeights: customFontWeights,
  gutters,
  lineHeights: customLineHeights,
  responsive,
  rowMaxWidth: '1440px',
  spacing,
  styles: {
    // @ts-ignore must provide null style values to override
    global: globalStyles
  },
  utility: {
    convertHexToRgba
  },
  zIndices: {
    // The Pendo lightbulb button appears in the screen's bottom-right.
    // The Drawer appears on the bottom-right too, and it needs to render on top of this button.
    // The Pendo lightbulb button uses a z-index of 1999, so we use 2000 for the Drawer:
    modal: 2000
  }
});
export default spTheme;